const FINISHED_STOP_STATUSES = ["completed", "skipped"];
const TASK_STATUSES = ["draft", "planned", "active", "blocked", "completed", "cancelled"];
const PRIORITIES = ["low", "normal", "high", "critical"];
const STOP_KINDS = ["pickup", "delivery", "waypoint", "service", "border", "other"];
const STOP_STATUSES = ["pending", "arrived", "completed", "skipped", "blocked"];
const ROUTE_PLAN_STATUSES = ["active", "superseded"];
const INSTRUCTION_STATUSES = ["active", "superseded", "cancelled"];
const MESSAGE_DIRECTIONS = ["inbound", "outbound", "internal"];
const MESSAGE_CHANNELS = ["discord", "email", "whatsapp", "phone", "system", "other"];
const EXCEPTION_SEVERITIES = ["info", "warning", "critical"];
const EXCEPTION_STATUSES = ["open", "acknowledged", "resolved", "dismissed"];
const ACTIVE_EXCEPTION_STATUSES = ["open", "acknowledged"];

export const UNKNOWN = "unknown";
export const TRANSPORT_TASK_CONTRACT_VERSION = "1.0.0";

function isObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function isPositiveSafeInteger(value) {
  return Number.isSafeInteger(value) && value >= 1;
}

function isOptionalString(value) {
  return value === undefined || typeof value === "string";
}

function isOptionalTimestamp(value) {
  return value === undefined || isTimestamp(value);
}

function isTimestamp(value) {
  return isNonEmptyString(value) && !Number.isNaN(new Date(value).getTime());
}

function validateStopEvent(event) {
  return isObject(event)
    && isNonEmptyString(event.stop_event_id)
    && isNonEmptyString(event.from_status)
    && isNonEmptyString(event.to_status)
    && isTimestamp(event.observed_at)
    && isNonEmptyString(event.source)
    && isNonEmptyString(event.evidence_ref)
    && isNonEmptyString(event.confirmed_by)
    && isTimestamp(event.created_at);
}

function validateStop(stop) {
  return isObject(stop)
    && isNonEmptyString(stop.stop_id)
    && isPositiveSafeInteger(stop.sequence)
    && STOP_KINDS.includes(stop.kind)
    && isNonEmptyString(stop.location_name)
    && STOP_STATUSES.includes(stop.status)
    && Array.isArray(stop.events)
    && stop.events.every(validateStopEvent);
}

function validateRoutePlan(routePlan) {
  return isObject(routePlan)
    && isNonEmptyString(routePlan.route_plan_id)
    && isPositiveSafeInteger(routePlan.version)
    && ROUTE_PLAN_STATUSES.includes(routePlan.status)
    && isNonEmptyString(routePlan.reason)
    && isNonEmptyString(routePlan.source)
    && isNonEmptyString(routePlan.evidence_ref)
    && isTimestamp(routePlan.effective_at)
    && isNonEmptyString(routePlan.created_by)
    && isTimestamp(routePlan.created_at)
    && Array.isArray(routePlan.stops)
    && routePlan.stops.every(validateStop);
}

function validateAssignment(assignment) {
  return isObject(assignment)
    && isNonEmptyString(assignment.assignment_id)
    && isNonEmptyString(assignment.vehicle_ref)
    && isOptionalString(assignment.driver_display_name)
    && isTimestamp(assignment.effective_at)
    && isNonEmptyString(assignment.source)
    && isNonEmptyString(assignment.evidence_ref);
}

function validateInstruction(instruction) {
  return isObject(instruction)
    && isNonEmptyString(instruction.instruction_id)
    && isPositiveSafeInteger(instruction.version)
    && isNonEmptyString(instruction.content)
    && isNonEmptyString(instruction.source)
    && isNonEmptyString(instruction.evidence_ref)
    && INSTRUCTION_STATUSES.includes(instruction.status)
    && isTimestamp(instruction.effective_at)
    && isOptionalTimestamp(instruction.cancelled_at)
    && isOptionalString(instruction.cancel_reason)
    && isNonEmptyString(instruction.created_by)
    && isTimestamp(instruction.created_at);
}

function validateEvidence(evidence) {
  return isObject(evidence)
    && isNonEmptyString(evidence.evidence_id)
    && isNonEmptyString(evidence.evidence_type)
    && isNonEmptyString(evidence.source_system)
    && isNonEmptyString(evidence.source_ref)
    && isTimestamp(evidence.observed_at)
    && isNonEmptyString(evidence.summary)
    && isTimestamp(evidence.created_at);
}

function validateDeliveryEvent(event) {
  return isObject(event)
    && isNonEmptyString(event.delivery_event_id)
    && isNonEmptyString(event.status)
    && isTimestamp(event.observed_at)
    && isNonEmptyString(event.source)
    && isNonEmptyString(event.evidence_ref)
    && isNonEmptyString(event.recorded_by)
    && isTimestamp(event.created_at);
}

function validateMessage(message) {
  return isObject(message)
    && isNonEmptyString(message.message_id)
    && MESSAGE_DIRECTIONS.includes(message.direction)
    && MESSAGE_CHANNELS.includes(message.channel)
    && isNonEmptyString(message.body_summary)
    && isTimestamp(message.occurred_at)
    && isNonEmptyString(message.initial_delivery_status)
    && Array.isArray(message.delivery_events)
    && message.delivery_events.every(validateDeliveryEvent)
    && isTimestamp(message.created_at);
}

function validateExceptionEvent(event) {
  return isObject(event)
    && isNonEmptyString(event.exception_event_id)
    && isNonEmptyString(event.from_status)
    && isNonEmptyString(event.to_status)
    && isTimestamp(event.observed_at)
    && isNonEmptyString(event.source)
    && isNonEmptyString(event.evidence_ref)
    && isNonEmptyString(event.recorded_by)
    && isTimestamp(event.created_at);
}

function validateException(exception) {
  return isObject(exception)
    && isNonEmptyString(exception.exception_id)
    && isNonEmptyString(exception.code)
    && EXCEPTION_SEVERITIES.includes(exception.severity)
    && EXCEPTION_STATUSES.includes(exception.status)
    && isNonEmptyString(exception.description)
    && isTimestamp(exception.detected_at)
    && isTimestamp(exception.created_at)
    && Array.isArray(exception.events)
    && exception.events.every(validateExceptionEvent);
}

function validateSchedule(schedule) {
  if (!isObject(schedule)) return false;
  const seriesPresent = schedule.series_id !== undefined && schedule.series_id !== null;
  const ownerPresent = schedule.scheduler_owner !== undefined && schedule.scheduler_owner !== null;
  const reporting = schedule.reporting_instruction;
  return (schedule.next_check_at === undefined || schedule.next_check_at === null || isTimestamp(schedule.next_check_at))
    && (schedule.series_id === undefined || schedule.series_id === null || isNonEmptyString(schedule.series_id))
    && (schedule.scheduler_owner === undefined || schedule.scheduler_owner === null || ["nanoclaw", "external", "manual"].includes(schedule.scheduler_owner))
    && seriesPresent === ownerPresent
    && (reporting === undefined || (
      isObject(reporting)
      && isNonEmptyString(reporting.instruction)
      && isOptionalString(reporting.channel)
      && isOptionalString(reporting.cadence)
      && (reporting.active === undefined || typeof reporting.active === "boolean")
    ));
}

function validateTerminalEvent(event, taskStatus) {
  if (!isObject(event) || event.status !== taskStatus) return false;
  const outcomeValid = event.status === "completed"
    ? isNonEmptyString(event.outcome)
    : event.outcome === undefined;
  return ["completed", "cancelled"].includes(event.status)
    && isNonEmptyString(event.terminal_event_id)
    && isNonEmptyString(event.evidence_id)
    && outcomeValid
    && isNonEmptyString(event.reason)
    && isTimestamp(event.occurred_at)
    && isNonEmptyString(event.recorded_by)
    && isTimestamp(event.created_at);
}

export function parseStrongNumericEtag(etag) {
  if (typeof etag !== "string") return null;
  const match = etag.match(/^"([1-9][0-9]*)"$/);
  if (!match) return null;
  const version = Number(match[1]);
  return isPositiveSafeInteger(version) ? version : null;
}

export function validateTaskSummary(summary) {
  if (!isObject(summary)) return false;
  const ownerValid = summary.owner_agent_profile === undefined
    || summary.owner_agent_profile === null
    || ["junior", "senior"].includes(summary.owner_agent_profile);
  return isNonEmptyString(summary.transport_task_id)
    && isNonEmptyString(summary.customer_ref)
    && isNonEmptyString(summary.order_ref)
    && isNonEmptyString(summary.title)
    && TASK_STATUSES.includes(summary.status)
    && PRIORITIES.includes(summary.priority)
    && isPositiveSafeInteger(summary.version)
    && isTimestamp(summary.updated_at)
    && (summary.next_check_at === undefined || isTimestamp(summary.next_check_at))
    && ownerValid;
}

export function validateTaskListResponse(response, contractVersion) {
  return contractVersion === TRANSPORT_TASK_CONTRACT_VERSION
    && isObject(response)
    && Array.isArray(response.items)
    && response.items.every(validateTaskSummary);
}

export function validateTransportTask(task) {
  if (!validateTaskSummary(task)) return false;
  const requiredStrings = ["source_system", "source_ref", "created_by", "updated_by"];
  const requiredArrays = ["stops", "route_plans", "instructions", "assignments", "evidence", "messages", "exceptions"];
  const isTerminalTask = ["completed", "cancelled"].includes(task.status);
  const terminalValid = isTerminalTask
    ? validateTerminalEvent(task.terminal_event, task.status)
    : task.terminal_event === undefined || task.terminal_event === null;
  return requiredStrings.every(key => isNonEmptyString(task[key]))
    && isTimestamp(task.created_at)
    && requiredArrays.every(key => Array.isArray(task[key]))
    && task.stops.every(validateStop)
    && task.route_plans.every(validateRoutePlan)
    && task.instructions.every(validateInstruction)
    && task.assignments.every(validateAssignment)
    && (task.assignment === undefined || task.assignment === null || validateAssignment(task.assignment))
    && (task.schedule === undefined || task.schedule === null || validateSchedule(task.schedule))
    && task.evidence.every(validateEvidence)
    && task.messages.every(validateMessage)
    && task.exceptions.every(validateException)
    && terminalValid;
}

export function validateDetailSnapshot(task, etag, expectedTaskId = null, minimumVersion = null, contractVersion = null) {
  const minimumValid = minimumVersion === null || minimumVersion === undefined || isPositiveSafeInteger(minimumVersion);
  return contractVersion === TRANSPORT_TASK_CONTRACT_VERSION
    && validateTransportTask(task)
    && (!expectedTaskId || task.transport_task_id === expectedTaskId)
    && minimumValid
    && (minimumVersion === null || minimumVersion === undefined || task.version >= minimumVersion)
    && parseStrongNumericEtag(etag) === task.version;
}

function authorityError(kind) {
  return Object.assign(new Error(kind), { kind });
}

export async function loadAtomicDetail(fetchDetail, expectedTaskId = null, minimumVersion = null) {
  const first = await fetchDetail();
  if (!first || first.contractVersion !== TRANSPORT_TASK_CONTRACT_VERSION || !validateTransportTask(first.data) || (expectedTaskId && first.data.transport_task_id !== expectedTaskId)) throw authorityError("contract");
  if (validateDetailSnapshot(first.data, first.etag, expectedTaskId, minimumVersion, first.contractVersion)) return first;

  const second = await fetchDetail();
  if (!second || second.contractVersion !== TRANSPORT_TASK_CONTRACT_VERSION || !validateTransportTask(second.data) || (expectedTaskId && second.data.transport_task_id !== expectedTaskId)) throw authorityError("contract");
  if (!validateDetailSnapshot(second.data, second.etag, expectedTaskId, minimumVersion, second.contractVersion)) throw authorityError("stale");
  return second;
}

function allStops(task) {
  if (!task) return [];
  const stops = Array.isArray(task.stops) ? task.stops : [];
  const routeStops = (Array.isArray(task.route_plans) ? task.route_plans : [])
    .reduce((all, routePlan) => all.concat(Array.isArray(routePlan.stops) ? routePlan.stops : []), []);
  return stops.concat(routeStops);
}

export function uniqueStopEvents(task) {
  const seen = new Set();
  return allStops(task).reduce((events, stop) => {
    (Array.isArray(stop.events) ? stop.events : []).forEach(event => {
      if (!event || !isNonEmptyString(event.stop_event_id) || seen.has(event.stop_event_id)) return;
      seen.add(event.stop_event_id);
      events.push({ ...event, stop });
    });
    return events;
  }, []);
}

export function nextStop(task) {
  if (!task || !Array.isArray(task.stops)) return null;
  return task.stops
    .slice()
    .sort((a, b) => a.sequence - b.sequence)
    .find(stop => !FINISHED_STOP_STATUSES.includes((stop.status || "").toLowerCase())) || null;
}

export function latestVerifiedEvent(task) {
  return uniqueStopEvents(task)
    .filter(event => event.evidence_ref && event.confirmed_by)
    .sort((a, b) => new Date(b.observed_at) - new Date(a.observed_at))[0] || null;
}

export function openExceptions(task) {
  if (!task || !Array.isArray(task.exceptions)) return [];
  return task.exceptions.filter(exception => ACTIVE_EXCEPTION_STATUSES.includes((exception.status || "").toLowerCase()));
}

export function taskDeadline(task, stop) {
  if (stop) return stop.window_end || stop.window_start || null;
  return task ? task.planned_end || null : null;
}

export function deadlineState(deadline, now = new Date()) {
  if (!deadline) return UNKNOWN;
  const parsed = new Date(deadline);
  if (Number.isNaN(parsed.getTime())) return UNKNOWN;
  return parsed < now ? "overdue" : "scheduled";
}

function summaryFromDetail(detail) {
  return {
    transport_task_id: detail.transport_task_id,
    external_ref: detail.external_ref,
    customer_ref: detail.customer_ref,
    order_ref: detail.order_ref,
    title: detail.title,
    status: detail.status,
    priority: detail.priority,
    owner_agent_profile: detail.owner_agent_profile,
    next_check_at: detail.next_check_at,
    version: detail.version,
    updated_at: detail.updated_at,
  };
}

export function deriveTaskRow(summary, detail, etag, now = new Date(), detailState = null, contractVersion = null) {
  const hasDetail = validateDetailSnapshot(detail, etag, summary.transport_task_id, summary.version, contractVersion);
  const authoritativeSummary = hasDetail ? summaryFromDetail(detail) : summary;
  const stop = hasDetail ? nextStop(detail) : null;
  const deadline = hasDetail ? taskDeadline(detail, stop) : null;
  return {
    summary: authoritativeSummary,
    detail: hasDetail ? detail : null,
    etag: hasDetail ? etag : null,
    detailState: hasDetail ? "loaded" : detailState || "unknown",
    uncertain: !hasDetail,
    assignment: hasDetail && detail.assignment ? detail.assignment : null,
    nextStop: stop,
    deadline,
    deadlineState: hasDetail ? deadlineState(deadline, now) : UNKNOWN,
    latestVerifiedEvent: hasDetail ? latestVerifiedEvent(detail) : null,
    exceptions: hasDetail ? openExceptions(detail) : null,
  };
}

function timelineTime(item) {
  return item.time ? new Date(item.time).getTime() : 0;
}

export function buildTimeline(task) {
  if (!task) return [];
  const items = [];

  (task.instructions || []).forEach(instruction => items.push({
    id: `instruction-${instruction.instruction_id}`,
    kind: "instruction",
    time: instruction.effective_at || instruction.created_at,
    title: instruction.content,
    source: instruction.source,
    evidenceRef: instruction.evidence_ref,
    version: instruction.version,
    status: instruction.status,
  }));

  const assignments = task.assignments && task.assignments.length
    ? task.assignments
    : task.assignment ? [task.assignment] : [];
  assignments.forEach(assignment => items.push({
    id: `assignment-${assignment.assignment_id}`,
    kind: "assignment",
    time: assignment.effective_at,
    title: [assignment.vehicle_ref, assignment.driver_display_name || assignment.driver_id, assignment.trailer_ref || assignment.equipment_ref]
      .filter(Boolean).join(" · "),
    source: assignment.source,
    evidenceRef: assignment.evidence_ref,
    status: assignment.ended_at ? "ended" : "current",
  }));

  (task.route_plans || []).forEach(routePlan => items.push({
    id: `route-plan-${routePlan.route_plan_id}`,
    kind: "routePlan",
    time: routePlan.effective_at || routePlan.created_at,
    title: routePlan.reason,
    source: routePlan.source,
    evidenceRef: routePlan.evidence_ref,
    version: routePlan.version,
    status: routePlan.status,
  }));

  uniqueStopEvents(task).forEach(event => items.push({
    id: `stop-${event.stop_event_id}`,
    kind: "stopEvent",
    time: event.observed_at,
    title: `${event.stop.location_name}: ${event.from_status} → ${event.to_status}`,
    source: event.source,
    evidenceRef: event.evidence_ref,
    recordedBy: event.confirmed_by,
  }));

  (task.evidence || []).forEach(evidence => items.push({
    id: `evidence-${evidence.evidence_id}`,
    kind: "evidence",
    time: evidence.observed_at || evidence.created_at,
    title: evidence.summary,
    source: evidence.source_system,
    evidenceRef: evidence.source_ref,
  }));

  (task.messages || []).forEach(message => items.push({
    id: `message-${message.message_id}`,
    kind: "message",
    time: message.occurred_at,
    title: message.body_summary,
    source: message.channel,
    status: message.initial_delivery_status,
  }));

  (task.messages || []).forEach(message => (message.delivery_events || []).forEach(event => items.push({
    id: `delivery-${event.delivery_event_id}`,
    kind: "deliveryEvent",
    time: event.observed_at,
    title: event.detail || event.status,
    source: event.source,
    evidenceRef: event.evidence_ref,
    status: event.status,
    recordedBy: event.recorded_by,
  })));

  (task.exceptions || []).forEach(exception => items.push({
    id: `exception-${exception.exception_id}`,
    kind: "exception",
    time: exception.detected_at,
    title: exception.description,
    source: exception.code,
    status: exception.status,
    severity: exception.severity,
  }));

  (task.exceptions || []).forEach(exception => (exception.events || []).forEach(event => items.push({
    id: `exception-event-${event.exception_event_id}`,
    kind: "exceptionEvent",
    time: event.observed_at,
    title: `${exception.code}: ${event.from_status} → ${event.to_status}`,
    source: event.source,
    evidenceRef: event.evidence_ref,
    status: event.to_status,
    recordedBy: event.recorded_by,
    reason: event.resolution,
  })));

  if (task.terminal_event) {
    const event = task.terminal_event;
    items.push({
      id: `terminal-${event.terminal_event_id}`,
      kind: event.status === "cancelled" ? "cancelled" : "completed",
      time: event.occurred_at,
      title: event.status,
      status: event.status,
      evidenceRef: event.evidence_id,
      outcome: event.outcome,
      reason: event.reason,
      recordedBy: event.recorded_by,
    });
  } else if (task.completed_at) {
    items.push({
      id: `legacy-completed-${task.transport_task_id}`,
      kind: "completedLegacy",
      time: task.completed_at,
      title: task.status,
      status: task.status,
      uncertain: true,
      authoritative: false,
    });
  }

  return items.sort((a, b) => timelineTime(b) - timelineTime(a));
}
